class BlogPostLoadMore {
    /**
     * @param {HTMLElement} buttonElement
     */
    constructor(buttonElement) {
        this.buttonElement = buttonElement;
        this._apiData = null;
        this._renderedList = null;
        this._buttonIcon = null;
        this._isLoading = false;

        this.init();
    }

    /**
     * @returns {Object}
     */
    get apiData() {
        if (this._apiData === null) {
            const _apiData = {
                exclude: [],
                hasMore: true,
                page: 1,
            };

            if (window.blogPostApiData) {
                Object.assign(_apiData, window.blogPostApiData);
            }

            this._apiData = _apiData;
        }

        return this._apiData;
    }

    get apiDataNextPage() {
        const currentPage = this.apiData.page;

        if (!(currentPage > 0)) { // Avoid NaN value error
            return 1;
        }

        return currentPage + 1;
    }

    /**
     * @returns {HTMLElement|undefined}
     */
    get renderedList() {
        if (this._renderedList === null) {
            const _renderedList = document.getElementById('blogPostRenderedList');

            this._renderedList = _renderedList ? _renderedList : undefined;
        }

        return this._renderedList;
    }

    /**
     * @returns {URL}
     */
    get apiEndpointUrl() {
        const url = new URL('/wp-json/mblink/blog-posts', window.location.origin);
        const params = url.searchParams;

        params.set('page', this.apiDataNextPage);

        if (Array.isArray(this.apiData.exclude) && this.apiData.exclude.length > 0) {
            params.set('exclude', this.apiData.exclude.join(','));
        }

        return url;
    }

    /**
     * @returns {HTMLElement|undefined}
     */
    get buttonIcon() {
        if (this._buttonIcon === null) {
            const _buttonIcon = this.buttonElement.getElementsByClassName('icon').item(0);

            this._buttonIcon = _buttonIcon ? _buttonIcon : undefined;
        }

        return this._buttonIcon;
    }

    /**
     * @returns {Boolean}
     */
    get isLoading() {
        return this._isLoading;
    }

    /**
     * Set loading state.
     * @param value {Boolean}
     */
    set isLoading(value) {
        if (value === true) {
            this._isLoading = true;
            this.buttonElement.setAttribute('disabled', 'disabled');
            if (this.buttonIcon) {
                this.buttonIcon.style.display = 'block';
            }
        } else {
            this._isLoading = false;
            this.buttonElement.removeAttribute('disabled');
            if (this.buttonIcon) {
                this.buttonIcon.style.display = 'none';
            }
        }
    }

    async init() {
        this.buttonElement.addEventListener('click', (event) => {
            event.preventDefault();
            this.loadMorePosts();
        });
    }

    /**
     * @returns {Promise<any[]>}
     */
    async fetchPosts() {
        const respData = await fetch(this.apiEndpointUrl, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
            mode: 'same-origin',
        }).then(response => {
            if (response.ok) {
                return response.json();
            } else {
                return null;
            }
        });

        if (respData) {
            this.apiData.hasMore = !!respData.hasMore;
            this.apiData.page = respData.page ? respData.page : 0;

            return respData.data;
        }

        return [];
    }

    /**
     * @param {any[]} posts Array of post objects that was fetched from Rest API
     */
    async renderPosts(posts) {
        if (this.renderedList && posts.length > 0) {
            let renderedPosts = '';
            posts.forEach(post => {
                renderedPosts += post.rendered;
            });

            /**
             * @type {HTMLElement}
             */
            const lastRenderedListItem = this.renderedList.lastElementChild;

            lastRenderedListItem?.insertAdjacentHTML('afterend', renderedPosts);
        }
    }

    async loadMorePosts() {
        if (this.apiData.hasMore && !this.isLoading) {
            requestAnimationFrame(() => {
                this.isLoading = true;
            });

            const posts = await this.fetchPosts();
            await this.renderPosts(posts);

            requestAnimationFrame(() => {
                this.isLoading = false;

                if (!this.apiData.hasMore) {
                    this.buttonElement.style.display = 'none';
                }
            });
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const loadMoreButton = document.getElementById('blogPostLoadMore');
    if (loadMoreButton) {
        const loadMoreComponent = new BlogPostLoadMore(loadMoreButton);
    }
}, { passive: true, once: true });
